import React, { useState } from 'react';
import { motion, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { Trash2, Check, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SwipeableNotificationItemProps {
  children: React.ReactNode;
  onSwipeLeft: () => void;  // Delete
  onSwipeRight: () => void; // Mark as read
  isRead: boolean;
}

const SWIPE_THRESHOLD = 80;

const SwipeableNotificationItem: React.FC<SwipeableNotificationItemProps> = ({
  children,
  onSwipeLeft,
  onSwipeRight,
  isRead,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const x = useMotionValue(0);
  
  // Transform for left action (delete)
  const leftActionOpacity = useTransform(x, [-SWIPE_THRESHOLD, -40, 0], [1, 0.5, 0]);
  const leftActionScale = useTransform(x, [-SWIPE_THRESHOLD, -40, 0], [1, 0.8, 0.5]);
  
  // Transform for right action (mark as read)
  const rightActionOpacity = useTransform(x, [0, 40, SWIPE_THRESHOLD], [0, 0.5, 1]);
  const rightActionScale = useTransform(x, [0, 40, SWIPE_THRESHOLD], [0.5, 0.8, 1]);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (_: any, info: PanInfo) => {
    setIsDragging(false);
    
    if (info.offset.x < -SWIPE_THRESHOLD) {
      // Swipe left - delete
      onSwipeLeft();
    } else if (info.offset.x > SWIPE_THRESHOLD && !isRead) {
      // Swipe right - mark as read
      onSwipeRight();
    }
  };

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* Left action background (delete - red) */}
      <motion.div
        className="absolute inset-y-0 right-0 w-20 bg-red-500/20 flex items-center justify-center rounded-r-xl"
        style={{ opacity: leftActionOpacity }}
      >
        <motion.div style={{ scale: leftActionScale }}>
          <Trash2 className="w-6 h-6 text-red-400" />
        </motion.div>
      </motion.div>

      {/* Right action background (mark as read - green) */}
      {!isRead && (
        <motion.div
          className="absolute inset-y-0 left-0 w-20 bg-green-500/20 flex items-center justify-center rounded-l-xl"
          style={{ opacity: rightActionOpacity }}
        >
          <motion.div style={{ scale: rightActionScale }}>
            <Check className="w-6 h-6 text-green-400" />
          </motion.div>
        </motion.div>
      )}

      {/* Swipeable content */}
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.3}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        style={{ x, touchAction: 'pan-x pan-y' }}
        className={cn(
          'relative z-10',
          isDragging && 'cursor-grabbing'
        )}
        whileTap={{ cursor: 'grabbing' }}
      >
        {children}
      </motion.div>

      {/* Swipe hint on first unread notification */}
      {!isRead && (
        <div className="absolute inset-0 pointer-events-none">
          {/* Optional: Add swipe hint animation here */}
        </div>
      )}
    </div>
  );
};

export default SwipeableNotificationItem;
